/**
 * AI Resource Generation Service
 *
 * This service uses Google Gemini with Maps Grounding to generate intelligent, contextual external resources
 * for places based on trip data. The service calls Gemini directly from the client using domain-restricted API keys.
 *
 * Features:
 * - Google Gemini AI with Maps Grounding, coordinate-anchored via toolConfig
 * - Two resource categories: `general` and `restaurants` (that is all the pipeline
 *   emits - `configManager` declares others but `getCategoriesForContext()` returns
 *   only these two)
 * - In-memory caching with per-category TTL
 * - Graceful degradation to mock data on most failures (NOT on quota - that throws)
 *
 * Deliberately NOT claimed:
 * - No JSON schema enforcement. Output is unconstrained text parsed by a hand-rolled
 *   bracket scanner, which is why the truncation-salvage path exists.
 * - No URL validation on the live path. Every resource links to a Google search
 *   (see ALWAYS_USE_SEARCH_LINKS), so there is no destination URL left to validate.
 */

// ⚠️ THE ONLY SANCTIONED USE OF THIS IMPORT IN packages/places.
// It is banned by `no-restricted-imports` (see the packages/places override in
// .eslintrc.json) because it puts the Gemini API key in the published browser
// bundle. This one site is grandfathered in - do NOT copy the pattern, and do NOT
// silence the rule anywhere else.
//
// Status: an ACCEPTED design tier, not an open vulnerability - the key is
// API- and referrer-restricted and was rotated 2026-05-06. Routing through the
// generatePlaceResources Cloud Function is optional hardening, not a required fix.
// See "The client-side Gemini key" in packages/places/CLAUDE.md, and
// Documentation/01-Architecture/Security/apiKeyUsage.md for live key status.
// eslint-disable-next-line no-restricted-imports
import { GoogleGenerativeAI } from '@google/generative-ai';
import { db, doc, getDoc, updateDoc } from '@trip-planner-monorepo/shared/firebase/firebaseInit';
import { tripContextService } from './tripContextService.js';
import { wikitravelValidationService } from './wikitravelValidationService.js';
import { configManager, getCategoriesForContext } from './configManager.js';
import { filterDeletedResources, getDeletedResourceUrls } from './resourceDeletionService.js';

/**
 * Cache for storing generated resources
 * Structure: { cacheKey: { resources: [], timestamp: number, expiresAt: number } }
 */
const resourceCache = new Map();

/**
 * Generates cache key for a specific request
 * @param {string} userId - User ID
 * @param {string} tripId - Trip ID
 * @param {string} placeId - Place ID
 * @param {string} category - Resource category
 * @param {Object} tripContext - Trip context object
 * @returns {string} Cache key
 */
function generateCacheKey(userId, tripId, placeId, category, tripContext) {
  const contextHash = tripContext.formattedDateRange || 'no-dates';
  return `${userId}:${tripId}:${placeId}:${category}:${contextHash}`;
}

/**
 * Checks if cached resources are still valid
 * @param {string} cacheKey - Cache key
 * @param {string} category - Resource category
 * @returns {Promise<Object|null>} Cached resources if valid, null otherwise
 */
async function getCachedResources(cacheKey, category) {
  console.log(`🔍 [AIResourceGeneration] Checking cache for key: ${cacheKey}`);
  const cached = resourceCache.get(cacheKey);
  
  if (!cached) {
    console.log(`🔍 [AIResourceGeneration] No cache entry found for ${category}`);
    return null;
  }
  
  console.log(`🔍 [AIResourceGeneration] Found cache entry for ${category}:`, cached);
  
  // Check if cache entry has expired
  const cacheConfig = await configManager.getCacheConfig();
  const ttl = cacheConfig.ttl[category] || cacheConfig.ttl.general;
  if (Date.now() > cached.expiresAt) {
    console.log(`🔍 [AIResourceGeneration] Cache expired for ${category}, deleting`);
    resourceCache.delete(cacheKey);
    return null;
  }
  
  console.log(`🔍 [AIResourceGeneration] Cache valid for ${category}, returning resources`);
  return cached;
}

/**
 * Stores resources in cache
 * @param {string} cacheKey - Cache key
 * @param {Array} resources - Generated resources
 * @param {string} category - Resource category
 */
async function setCachedResources(cacheKey, resources, category) {
  const now = Date.now();
  const cacheConfig = await configManager.getCacheConfig();
  const ttl = cacheConfig.ttl[category] || cacheConfig.ttl.general;
  const expiresAt = now + ttl;
  
  resourceCache.set(cacheKey, {
    resources,
    timestamp: now,
    expiresAt,
    category
  });
  
  console.log(`💾 [AIResourceGeneration] Cached ${resources.length} resources for ${category} with key: ${cacheKey}`);
}

/**
 * Validates a URL by checking if it's accessible
 * @param {string} url - URL to validate
 * @returns {Promise<boolean>} True if URL is accessible
 */
async function validateUrl(url) {
  const validationConfig = await configManager.getValidationConfig();
  
  if (!validationConfig.validateUrls) {
    return true;
  }

  try {
    // Check if URL is in blocked domains
    const urlObj = new URL(url);
    const domain = urlObj.hostname.toLowerCase();
    
    for (const blockedDomain of validationConfig.blockedDomains) {
      if (domain.includes(blockedDomain.toLowerCase())) {
        console.log(`🚫 [AIResourceGeneration] Blocked domain: ${domain}`);
        return false;
      }
    }

    // Check if URL is in allowed domains (if specified)
    if (validationConfig.allowedDomains.length > 0 && 
        !validationConfig.allowedDomains.includes('*')) {
      const isAllowed = validationConfig.allowedDomains.some(allowedDomain => 
        domain.includes(allowedDomain.toLowerCase())
      );
      
      if (!isAllowed) {
        console.log(`🚫 [AIResourceGeneration] Domain not allowed: ${domain}`);
        return false;
      }
    }

    // Validate URL accessibility
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), validationConfig.timeout);

    const response = await fetch(url, {
      method: 'HEAD',
      signal: controller.signal,
      headers: {
        'User-Agent': 'Mozilla/5.0 (compatible; TripPlanner/1.0)'
      }
    });

    clearTimeout(timeoutId);

    const isValid = response.status >= 200 && response.status < 300;
    console.log(`🔍 [AIResourceGeneration] URL validation: ${url} - ${response.status} - ${isValid ? 'Valid' : 'Invalid'}`);
    
    return isValid;
  } catch (error) {
    console.warn(`⚠️ [AIResourceGeneration] URL validation failed for ${url}:`, error.message);
    return false;
  }
}

/**
 * Parses AI response to extract resources
 * @param {string} response - AI response text
 * @param {string} category - Resource category
 * @returns {Array} Array of resource objects
 */
function parseAIResponse(response, category) {
  try {
    console.log(`🔍 [AIResourceGeneration] Parsing response for ${category}:`, response.substring(0, 200));

    // Try to parse as JSON first
    if (response.trim().startsWith('{') || response.trim().startsWith('[')) {
      try {
        const jsonResponse = JSON.parse(response);
        if (Array.isArray(jsonResponse)) {
          return jsonResponse.map(item => {
            let name = item.name || item.title || 'Resource';
            let url = item.url || item.link || item.website || '';

            // Clean JSON fragments from name (e.g., {"name": "Katz's", "url": ")
            if (name.includes('{"') || name.includes('"url"')) {
              // Extract just the actual name from malformed JSON
              const nameMatch = name.match(/(?:{"name"\s*:\s*"([^"]+)"|"([^"]+)")/);
              if (nameMatch) {
                name = nameMatch[1] || nameMatch[2];
              }
            }

            // Clean trailing commas and other artifacts from URL
            url = url.replace(/[,\s]+$/, '').replace(/["']/g, '').trim();

            // Remove any JSON artifacts from URL
            if (url.includes('"') || url.includes('}')) {
              url = url.split(/["'}]/)[0].trim();
            }

            return {
              name: name.trim(),
              url: url,
              description: item.description || '',
              category: category,
              source: 'ai'
            };
          }).filter(resource => resource.url && resource.url.startsWith('http'));
        }
      } catch (parseError) {
        console.error('❌ [AIResourceGeneration] JSON parse error:', parseError.message);
        console.error('❌ [AIResourceGeneration] Malformed JSON:', response.substring(0, 500));
        // Fall through to plain text parsing
      }
    }

    // Parse as plain text with URLs
    const urlRegex = /(https?:\/\/[^\s,;"']+)/g;
    const urls = response.match(urlRegex) || [];

    // Extract names from context around URLs
    const resources = urls.map(url => {
      // Clean URL of trailing punctuation/commas
      const cleanUrl = url.replace(/[,;."']+$/, '');

      // Try to extract name from text before URL
      const beforeUrl = response.substring(0, response.indexOf(url));
      const lines = beforeUrl.split('\n').filter(line => line.trim());
      let name = lines.length > 0 ? lines[lines.length - 1].trim() : 'Resource';

      // Remove common prefixes and JSON artifacts
      name = name.replace(/^\d+[\.\)]\s*/, '').replace(/^[-*]\s*/, '');
      name = name.replace(/(?:name|title)\s*:\s*/i, '');
      name = name.replace(/[{}"]/g, '').trim();

      return {
        name: name.length > 100 || name.length === 0 ? 'Resource' : name,
        url: cleanUrl,
        description: '',
        category: category,
        source: 'ai'
      };
    });

    // Remove duplicate URLs (keep first occurrence)
    const uniqueResources = [];
    const seenUrls = new Set();
    for (const resource of resources) {
      if (!seenUrls.has(resource.url)) {
        uniqueResources.push(resource);
        seenUrls.add(resource.url);
      } else {
        console.log(`🔄 [AIResourceGeneration] Removed duplicate URL: ${resource.url}`);
      }
    }

    console.log(`✅ [AIResourceGeneration] Parsed ${uniqueResources.length} unique resources for ${category} (removed ${resources.length - uniqueResources.length} duplicates)`);
    return uniqueResources;
  } catch (error) {
    console.error('❌ [AIResourceGeneration] Error parsing AI response:', error);
    console.error('❌ [AIResourceGeneration] Response was:', response);
    return [];
  }
}

/**
 * Generates mock resources for testing when API key is not available
 * @param {string} category - Resource category
 * @returns {Array} Mock resources
 */
function generateMockResources(category) {
  const mockResources = {
    general: [
      {
        id: 'mock_general_1',
        name: 'Official Tourism Website',
        url: 'https://example-tourism.com',
        description: 'Official tourism information and visitor guides'
      },
      {
        id: 'mock_general_2', 
        name: 'Cultural Guide',
        url: 'https://example-culture.com',
        description: 'Cultural attractions and local history'
      }
    ],
    restaurants: [
      {
        id: 'mock_restaurant_1',
        name: 'Local Bistro',
        url: 'https://example-bistro.com',
        description: 'Traditional local cuisine and atmosphere'
      },
      {
        id: 'mock_restaurant_2',
        name: 'Fine Dining Restaurant',
        url: 'https://example-finedining.com', 
        description: 'Upscale dining with modern cuisine'
      }
    ],
    attractions: [
      {
        id: 'mock_attraction_1',
        name: 'Historic Museum',
        url: 'https://example-museum.com',
        description: 'Local history and cultural exhibits'
      },
      {
        id: 'mock_attraction_2',
        name: 'Scenic Park',
        url: 'https://example-park.com',
        description: 'Beautiful natural landscapes and walking trails'
      }
    ],
    transportation: [
      {
        id: 'mock_transport_1',
        name: 'Public Transit',
        url: 'https://example-transit.com',
        description: 'Local bus and train services'
      }
    ],
    shopping: [
      {
        id: 'mock_shopping_1',
        name: 'Local Market',
        url: 'https://example-market.com',
        description: 'Traditional market with local goods'
      }
    ],
    nightlife: [
      {
        id: 'mock_nightlife_1',
        name: 'Local Bar',
        url: 'https://example-bar.com',
        description: 'Cozy bar with local drinks and atmosphere'
      }
    ]
  };

  return mockResources[category] || [];
}

// Resource count constants for Gemini API calls and retry logic
const GEMINI_INITIAL_MAX_RESOURCES = 10;
const GEMINI_RETRY_MAX_RESOURCES = 5;
const GEMINI_MIN_ACCEPTABLE_RESOURCES = 5; // retry threshold: fewer than this on truncation triggers a retry

/**
 * When true, every AI-generated resource links to a Google search for the resource
 * name rather than to the resource's own website.
 *
 * WHY: the direct URLs Gemini returns are frequently dead or wrong, so a search
 * result page is more reliably useful to the user than a broken link. Requested
 * 2026-08-17; the direct-link code is commented out rather than deleted, next to
 * each call site below, so it can be reinstated.
 *
 * ⚠️ TO REINSTATE DIRECT LINKS, THREE THINGS ARE NEEDED — not just this flag:
 *   1. Set this to false.
 *   2. Uncomment the two `DIRECT LINK PATH` blocks below.
 *   3. Restore the prompts to ask for a `url` field again — they were changed on
 *      2026-08-17 to explicitly forbid URLs, so with the flag off and the current
 *      prompts, the only source of direct URLs would be the grounding metadata.
 *
 * Full rationale and the exact blocks affected: "Search links vs direct links"
 * under "AI-Powered Resource Generation" in packages/places/CLAUDE.md.
 */
const ALWAYS_USE_SEARCH_LINKS = true;

/**
 * Builds a Google search URL for a resource, used both as the deliberate default
 * (see ALWAYS_USE_SEARCH_LINKS) and as the fallback when no usable URL exists.
 * @param {string} resourceName - Name of the resource
 * @param {string} locationContext - Geographic context to disambiguate the search
 * @returns {string} Google search URL
 */
function buildSearchUrl(resourceName, locationContext) {
  const searchQuery = `${resourceName} ${locationContext}`.trim();
  return `https://www.google.com/search?q=${encodeURIComponent(searchQuery)}`;
}

/**
 * Builds a resource object whose link is a Google search for the resource name.
 * @param {Object} item - Raw parsed item from the Gemini response
 * @param {string} resourceName - Resolved resource name
 * @param {string} category - Resource category
 * @param {string} locationContext - Geographic context for the search query
 * @param {string} source - Provenance tag, for debugging which path produced this
 * @returns {Object} Resource object marked as a search link
 */
function buildSearchResource(item, resourceName, category, locationContext, source) {
  return {
    name: resourceName,
    url: buildSearchUrl(resourceName, locationContext),
    description: item.description || '',
    category: category,
    source: source,
    isSearchFallback: true
  };
}

/**
 * Makes a single Gemini API call with Maps Grounding. Internal — use callGeminiAPI instead.
 * Returns { resources, wasTruncated } so callGeminiAPI can decide whether to retry.
 * @param {string} prompt - The configManager prompt (not used in Maps Grounding path — we build simpleQuery instead)
 * @param {string} category - Resource category
 * @param {string} placeName - Name of the place (for logging and context)
 * @param {string} placeGeoContext - Geographic context (e.g., "Metz, France")
 * @param {Object} placeCoordinates - Optional coordinates {lat, lng} for Maps Grounding
 * @param {number} maxCount - Maximum number of resources to request in this call
 * @returns {Promise<{resources: Array, wasTruncated: boolean}>}
 */
async function _callGeminiAPIOnce(prompt, category, placeName = '', placeGeoContext = '', placeCoordinates = null, maxCount = GEMINI_INITIAL_MAX_RESOURCES) {
  try {
    console.log(`🤖 [AIResourceGeneration] Calling Gemini API with Maps Grounding for ${category}`);

    // This key is compiled into the browser bundle. That is an ACCEPTED design tier,
    // not an open hole: it is restricted to the Gemini API and to our own HTTP
    // referrers, and was rotated 2026-05-06. Authoritative status lives in
    // Documentation/01-Architecture/Security/apiKeyUsage.md (rows 10 and 87) — check
    // that, NOT the dated April/May snapshots, which are to-do lists rather than
    // status reports and will tell you this is unmitigated when it is not.
    // Moving to the generatePlaceResources Cloud Function remains optional hardening.
    //
    // Do NOT fall back to REACT_APP_GOOGLE_PLACES_API_KEY here. That key is
    // restricted to the Places APIs by design; using it for Gemini would force
    // the Generative Language API to be enabled on it and defeat the restriction.
    // Never log any part of the key — not even a suffix.
    const apiKey = process.env.REACT_APP_GEMINI_API_KEY;

    if (!apiKey) {
      console.warn('⚠️ [AIResourceGeneration] REACT_APP_GEMINI_API_KEY not set. Using mock data.');
      return { resources: generateMockResources(category), wasTruncated: false };
    }

    // Initialize Gemini
    const genAI = new GoogleGenerativeAI(apiKey);
    const model = genAI.getGenerativeModel({
      model: "gemini-3.1-flash-lite"  // Maps Grounding supported on Gemini 3.1 Flash-Lite
    });

    // Build simple query for Maps Grounding
    // Maps Grounding works best with simple, direct queries
    const locationContext = placeGeoContext || placeName;
    let simpleQuery;

    // Build coordinates string for prompt
    const coordsString = placeCoordinates ? `latitude ${placeCoordinates.lat}, longitude ${placeCoordinates.lng}` : 'the provided coordinates';
    
    if (category === 'restaurants') {
      simpleQuery = `You are a local tourist guide for ${locationContext}.

Your task is to find up to ${maxCount} "popular", "well-known" or "highly-rated" restaurants or cafes near ${coordsString}.

You MUST use the Google Maps tool for this task.

For each place, provide its name and a brief 1-2 sentence description.

CRITICAL RULE:
Do NOT include a URL, website, link or 'url' field of any kind. Return only 'name' and 'description'.

Format your entire response as a single, valid JSON array of objects, and nothing else.

Here is an example of the exact format:
[
  {
    "name": "Bar George",
    "description": "Grand swanky wood-clad venue serving a Scottish & British menu with cocktails in a boutique hotel"
  },
  {
    "name": "Example Small Cafe",
    "description": "A cozy neighborhood cafe."
  }
]`;
    } else {
      simpleQuery = `You are a local tourist guide for ${locationContext}.

Your task is to find up to ${maxCount} "popular", "well-known" or "highly-rated" tourism attractions and visitor resources near ${coordsString}. Include museums, landmarks, parks, and tourism offices.

You MUST use the Google Maps tool for this task.

For each place, provide its name and a brief 1-2 sentence description.

CRITICAL RULE:
Do NOT include a URL, website, link or 'url' field of any kind. Return only 'name' and 'description'.

Format your entire response as a single, valid JSON array of objects, and nothing else.

Here is an example of the exact format:
[
  {
    "name": "Montreal Museum of Fine Arts",
    "description": "Spacious museum showcasing Québec and Canadian visual works, plus international contemporary art."
  },
  {
    "name": "Mount Royal Park",
    "description": "Sizable green space featuring trails, a lake with boat rentals and striking city panoramas."
  }
]`;
    }

    console.log(`🔍 [AIResourceGeneration] Query: ${simpleQuery}`);
    if (placeCoordinates) {
      console.log(`📍 [AIResourceGeneration] Using coordinates: ${placeCoordinates.lat}, ${placeCoordinates.lng}`);
    } else {
      console.warn('⚠️ [AIResourceGeneration] No coordinates provided - Maps Grounding may be less accurate');
    }

    // Build the API request configuration
    const requestConfig = {
      contents: [{ role: "user", parts: [{ text: simpleQuery }] }],
      tools: [{
        googleMaps: {}  // Use Maps Grounding (not googleSearch!)
      }],
      generationConfig: {
        temperature: 0.1,  // Low temperature for more factual, less creative responses
        maxOutputTokens: 8192  // Generous limit for Gemini 3.1 Flash-Lite thinking budget
      }
    };

    // Add coordinates to toolConfig if available (critical for accurate Maps Grounding)
    if (placeCoordinates && placeCoordinates.lat && placeCoordinates.lng) {
      requestConfig.toolConfig = {
        retrievalConfig: {
          latLng: {
            latitude: placeCoordinates.lat,
            longitude: placeCoordinates.lng
          }
        }
      };
      console.log(`✅ [AIResourceGeneration] Added coordinates to toolConfig for precise Maps Grounding`);
    }

    // Call Gemini with Maps Grounding tool
    const result = await model.generateContent(requestConfig);

    const response = await result.response;
    const text = response.text();

    // Log grounding metadata to verify Maps Grounding is working
    const metadata = response.candidates?.[0]?.groundingMetadata;
    
    // Build a map of place names to URLs from grounding metadata
    const groundingUrlMap = new Map();
    
    if (metadata?.groundingChunks?.length) {
      console.log(`✅ [AIResourceGeneration] Maps Grounding ACTIVE - ${metadata.groundingChunks.length} verified sources`);
      
      // Extract URLs from grounding chunks and map them by place name.
      // ONLY include official websites, NOT Google Maps URLs.
      // Skipped entirely while ALWAYS_USE_SEARCH_LINKS is on: the map's only consumer
      // is gated on the same flag, so this parsing would be pure wasted work.
      if (!ALWAYS_USE_SEARCH_LINKS) {
        metadata.groundingChunks.forEach(chunk => {
          const placeName = chunk.web?.title || chunk.maps?.title;
          // Prefer web.uri (official websites) over maps.uri (Google Maps URLs)
          let url = chunk.web?.uri || chunk.maps?.uri;

          // Filter out Google Maps URLs - we only want official websites
          if (url) {
            const isMapsUrl = url.includes('maps.google.com') ||
                             url.includes('google.com/maps') ||
                             url.includes('google.com/?cid=') ||
                             url.match(/^https?:\/\/.*google\.com.*[?&]cid=/);

            if (isMapsUrl) {
              // Skip Maps URLs - don't add them to the map
              return;
            }
          }

          if (placeName && url) {
            // Normalize place name for matching (lowercase, remove extra spaces)
            const normalizedName = placeName.toLowerCase().trim().replace(/\s+/g, ' ');
            groundingUrlMap.set(normalizedName, url);

            // Also try matching without common suffixes/prefixes
            const nameWithoutPrefixes = normalizedName
              .replace(/^(restaurant|restaurante|bar|café|cafe|gastrobar|tapas|museum|attraction|park|palace|basilica|oratory|cathedral|church)\s+/i, '')
              .replace(/\s+(restaurant|restaurante|bar|café|cafe|gastrobar|tapas|museum|attraction|park|palace|basilica|oratory|cathedral|church)$/i, '');
            if (nameWithoutPrefixes !== normalizedName) {
              groundingUrlMap.set(nameWithoutPrefixes, url);
            }
          }
        });

        console.log(`📍 [AIResourceGeneration] Extracted ${groundingUrlMap.size} URLs from grounding metadata`);
        console.log(`📍 [AIResourceGeneration] Sample grounding URLs:`, Array.from(groundingUrlMap.entries()).slice(0, 5));
      }

      const mapsSources = metadata.groundingChunks
        .filter(c => c.web?.uri || c.maps?.uri)
        .slice(0, 3)
        .map(c => ({
          title: c.web?.title || c.maps?.title,
          url: c.web?.uri || c.maps?.uri,
          placeId: c.maps?.placeId
        }));
      console.log(`📍 [AIResourceGeneration] Sample sources:`, mapsSources);
    } else {
      console.warn('⚠️ [AIResourceGeneration] No Maps Grounding metadata - fallback to web search likely occurred');
      console.warn('⚠️ This usually means coordinates are missing or inaccurate');
    }

    console.log(`✅ [AIResourceGeneration] Received response from Gemini for ${category}`);
    console.log(`📊 [AIResourceGeneration] Response length: ${text.length} characters`);

    // Strip markdown code block markers if present (Gemini sometimes wraps JSON in ```json```)
    let cleanedText = text.trim();
    if (cleanedText.startsWith('```')) {
      cleanedText = cleanedText
        .replace(/^```json\s*/i, '')  // Remove opening ```json
        .replace(/^```\s*/, '')         // Remove opening ```
        .replace(/\s*```\s*$/g, '');    // Remove closing ```
      console.log(`📝 [AIResourceGeneration] Stripped markdown code blocks from response`);
    }

    // Gemini sometimes returns duplicate arrays or adds text after the JSON
    // We need to extract only the FIRST complete JSON array
    const jsonStart = cleanedText.indexOf('[');

    if (jsonStart === -1) {
      console.error('❌ [AIResourceGeneration] No valid JSON array found in response');
      console.error('Response preview:', cleanedText.substring(0, 500));
      return { resources: generateMockResources(category), wasTruncated: false };
    }

    // Single forward pass: find the closing ']' of the outermost array (jsonEnd) and
    // simultaneously track the last structurally complete top-level object (lastCompleteObject).
    // Both pieces of information come out of one O(n) scan, avoiding a separate salvage scan.
    // { and [ both increment depth; } and ] decrement. A '}' returning to depth 1 closes a
    // top-level array element. A ']' reaching depth 0 closes the outer array.
    let bracketCount = 0;
    let jsonEnd = -1;
    let lastCompleteObject = -1;
    let inString = false;
    let escapeNext = false;

    for (let i = jsonStart; i < cleanedText.length; i++) {
      const char = cleanedText[i];

      if (escapeNext) {
        escapeNext = false;
        continue;
      }

      if (char === '\\') {
        escapeNext = true;
        continue;
      }

      if (char === '"' && !escapeNext) {
        inString = !inString;
        continue;
      }

      if (!inString) {
        if (char === '[' || char === '{') {
          bracketCount++;
        } else if (char === ']' || char === '}') {
          bracketCount--;
          if (bracketCount === 0 && char === ']') {
            jsonEnd = i;
            break;
          }
          if (bracketCount === 1 && char === '}') {
            lastCompleteObject = i;
          }
        }
      }
    }

    if (jsonEnd === -1) {
      console.error('❌ [AIResourceGeneration] Could not find matching closing bracket');
      console.error('Response may be truncated. Full response:', cleanedText);

      if (lastCompleteObject > jsonStart) {
        const salvaged = cleanedText.substring(jsonStart, lastCompleteObject + 1) + ']';
        console.log('🔧 [AIResourceGeneration] Attempting to salvage partial JSON');
        try {
          const parsed = JSON.parse(salvaged);
          console.log(`✅ [AIResourceGeneration] Salvaged ${parsed.length} items from truncated response`);
          const salvageResources = parsed.map(item => {
            let url = item.url || '';
            const resourceName = item.name || 'Resource';

            // Only treat as "N/A" if it's literally "N/A" (case-insensitive) or explicitly empty
            // Valid URLs should start with http:// or https://
            const isNA = url && (url.trim().toUpperCase() === 'N/A' || url.trim() === '');
            const isValidUrl = url && (url.trim().startsWith('http://') || url.trim().startsWith('https://'));

            if (ALWAYS_USE_SEARCH_LINKS || isNA || !isValidUrl) {
              return buildSearchResource(
                item, resourceName, category, locationContext,
                'gemini-maps-salvaged-search-fallback'
              );
            }

            // --- DIRECT LINK PATH (disabled 2026-08-17) ---------------------------
            // Reinstate by setting ALWAYS_USE_SEARCH_LINKS = false and uncommenting.
            // See "Search links vs direct links" under "AI-Powered Resource
            // Generation" in packages/places/CLAUDE.md.
            // return {
            //   name: resourceName,
            //   url: url.trim(),
            //   description: item.description || '',
            //   category: category,
            //   source: 'gemini-maps-salvaged',
            //   isSearchFallback: false
            // };
            // ---------------------------------------------------------------------

            // Safety net - see the matching comment on the main parse path below.
            // Distinct `source` so the "flag flipped but blocks still commented"
            // state is visible in logs rather than looking like a genuine fallback.
            return buildSearchResource(
              item, resourceName, category, locationContext,
              'gemini-maps-salvaged-search-fallback-safetynet'
            );
          }).filter(resource => resource.url && resource.url.startsWith('http'));
          return { resources: salvageResources, wasTruncated: true };
        } catch (salvageError) {
          console.error('❌ [AIResourceGeneration] Could not salvage partial JSON:', salvageError);
        }
      }

      return { resources: generateMockResources(category), wasTruncated: true };
    }

    // Extract only the first complete JSON array
    const jsonOnly = cleanedText.substring(jsonStart, jsonEnd + 1);
    console.log(`📝 [AIResourceGeneration] Extracted first complete JSON array from position ${jsonStart} to ${jsonEnd}`);

    // Debug: Log a preview of the JSON to see what Gemini is returning
    console.log(`🔍 [AIResourceGeneration] JSON preview (first 500 chars):`, jsonOnly.substring(0, 500));
    console.log(`🔍 [AIResourceGeneration] JSON preview (last 500 chars):`, jsonOnly.substring(Math.max(0, jsonOnly.length - 500)));
    if (process.env.NODE_ENV === 'development') {
      console.log(`🔍 [AIResourceGeneration] Full JSON response:`, jsonOnly);
    }

    // Parse the structured JSON response
    let parsedResources;
    try {
      parsedResources = JSON.parse(jsonOnly);
      console.log(`🔍 [AIResourceGeneration] Parsed ${parsedResources.length} resources from JSON`);
      // Log each parsed item to see what URLs Gemini returned
      parsedResources.forEach((item, index) => {
        // `item.url` is normally absent now - the prompts forbid URLs. Logged anyway so a
        // model that ignores that instruction is visible rather than silently discarded.
        console.log(`🔍 [AIResourceGeneration] Item ${index + 1}: name="${item.name}"${item.url ? `, unexpected url="${item.url}"` : ''}`);
      });
    } catch (parseError) {
      console.error('❌ [AIResourceGeneration] JSON parse failed. Full JSON:', jsonOnly);
      throw parseError;
    }

    // Transform to our format. While ALWAYS_USE_SEARCH_LINKS is on, every resource gets a
    // Google search link and the URL Gemini returned is discarded. With the flag off, the
    // original behaviour applies: prefer the returned URL, recover from grounding metadata,
    // and fall back to a search link only for "N/A"/invalid/Maps URLs.
    const resources = parsedResources.map(item => {
      let url = item.url || '';
      const resourceName = item.name || 'Resource';
      
      // Log what we received from Gemini for debugging
      console.log(`🔍 [AIResourceGeneration] Processing resource: "${resourceName}"`);
      
      // Treat as invalid if it's "N/A", empty, OR a Google Maps URL.
      // Only consulted when ALWAYS_USE_SEARCH_LINKS is off; kept for the reinstate path.
      const isNA = url && (url.trim().toUpperCase() === 'N/A' || url.trim() === '');
      const isMapsUrl = url && (
        url.includes('maps.google.com') ||
        url.includes('google.com/maps') ||
        url.includes('google.com/?cid=') ||
        /google\.com\/search\?q=/.test(url) === false && /\/maps\//.test(url)
      );
      const isValidUrl = url && !isMapsUrl && (url.trim().startsWith('http://') || url.trim().startsWith('https://'));
      
      if (isMapsUrl && !ALWAYS_USE_SEARCH_LINKS) {
        console.log(`🚫 [AIResourceGeneration] Filtering Google Maps URL from response for "${resourceName}": ${url}`);
      }

      // If URL is missing, N/A, a Maps URL, or otherwise invalid, try to find it in grounding metadata.
      // Skipped entirely while ALWAYS_USE_SEARCH_LINKS is on - the recovered URL would be discarded.
      if (!ALWAYS_USE_SEARCH_LINKS && (isNA || !isValidUrl) && groundingUrlMap.size > 0) {
        // Try to match resource name with grounding metadata
        const normalizedResourceName = resourceName.toLowerCase().trim().replace(/\s+/g, ' ');
        
        // Try exact match first
        let foundUrl = groundingUrlMap.get(normalizedResourceName);
        
        // Try partial match (resource name contains grounding name or vice versa)
        if (!foundUrl) {
          for (const [groundingName, groundingUrl] of groundingUrlMap.entries()) {
            if (normalizedResourceName.includes(groundingName) || groundingName.includes(normalizedResourceName)) {
              foundUrl = groundingUrl;
              console.log(`🔍 [AIResourceGeneration] Matched "${resourceName}" to grounding URL via partial match with "${groundingName}"`);
              break;
            }
          }
        }
        
        if (foundUrl) {
          // URLs in groundingUrlMap are already filtered to exclude Maps URLs
          // So if we found a URL here, it's an official website
          url = foundUrl;
          console.log(`✅ [AIResourceGeneration] Found official URL from grounding metadata for "${resourceName}": ${url}`);
          return {
            name: resourceName,
            url: url,
            description: item.description || '',
            category: category,
            source: 'gemini-maps-grounding',
            isSearchFallback: false
          };
        }
      }
      
      // Always link to a Google search for the resource (see ALWAYS_USE_SEARCH_LINKS),
      // and as the genuine fallback when no usable URL exists.
      if (ALWAYS_USE_SEARCH_LINKS || isNA || !isValidUrl) {
        console.log(`🔍 [AIResourceGeneration] Using search URL for "${resourceName}"`);
        return buildSearchResource(
          item, resourceName, category, locationContext,
          'gemini-maps-search-fallback'
        );
      }

      // --- DIRECT LINK PATH (disabled 2026-08-17) -------------------------------
      // Normal resource with official URL from the Gemini response. Reinstate by
      // setting ALWAYS_USE_SEARCH_LINKS = false and uncommenting; the grounding
      // metadata lookup above is gated on the same flag and will come back with it.
      // See "Search links vs direct links" under "AI-Powered Resource Generation"
      // in packages/places/CLAUDE.md.
      // console.log(`✅ [AIResourceGeneration] Using official URL from Gemini response for "${resourceName}": ${url}`);
      // return {
      //   name: resourceName,
      //   url: url.trim(),
      //   description: item.description || '',
      //   category: category,
      //   source: 'gemini-maps',
      //   isSearchFallback: false
      // };
      // --------------------------------------------------------------------------

      // Safety net: with the direct-link block above commented out, a resource that
      // HAS a valid URL would fall through and return undefined if the flag were
      // flipped without also uncommenting. Degrade to a search link instead, with a
      // distinct `source` so that intermediate state is visible in logs.
      return buildSearchResource(
        item, resourceName, category, locationContext,
        'gemini-maps-search-fallback-safetynet'
      );
    }).filter(resource => resource.url && resource.url.startsWith('http'));

    console.log(`✅ [AIResourceGeneration] Parsed ${resources.length} resources from Gemini Maps`);
    return { resources, wasTruncated: false };

  } catch (error) {
    console.error(`❌ [AIResourceGeneration] Gemini API error for ${category}:`, error);

    // Handle API key errors
    if (error.message && error.message.includes('API_KEY')) {
      console.warn('⚠️ [AIResourceGeneration] API key invalid or not configured. Using mock data.');
      return { resources: generateMockResources(category), wasTruncated: false };
    }

    // Handle rate limiting
    if (error.status === 429 || error.message?.includes('quota')) {
      console.error('❌ [AIResourceGeneration] Gemini API rate limit exceeded.');
      throw new Error('AI resource generation rate limit exceeded. Please try again later.');
    }

    // Fallback to mock data for other errors
    console.warn('⚠️ [AIResourceGeneration] Falling back to mock data due to error');
    return { resources: generateMockResources(category), wasTruncated: false };
  }
}

/**
 * Calls Gemini with Maps Grounding and retries with a smaller count if the response
 * was truncated and yielded fewer than GEMINI_MIN_ACCEPTABLE_RESOURCES items.
 * Returns a plain resource array — wasTruncated is an internal concern of this function.
 * @param {string} prompt - The configManager prompt (passed through to _callGeminiAPIOnce)
 * @param {string} category - Resource category
 * @param {string} placeName - Name of the place
 * @param {string} placeGeoContext - Geographic context
 * @param {Object} placeCoordinates - Optional coordinates {lat, lng} for Maps Grounding
 * @returns {Promise<Array>} Generated resources
 */
async function callGeminiAPI(prompt, category, placeName = '', placeGeoContext = '', placeCoordinates = null) {
  const { resources, wasTruncated } = await _callGeminiAPIOnce(
    prompt, category, placeName, placeGeoContext, placeCoordinates, GEMINI_INITIAL_MAX_RESOURCES
  );

  if (wasTruncated && resources.length < GEMINI_MIN_ACCEPTABLE_RESOURCES) {
    console.warn(`⚠️ [AIResourceGeneration] Truncated response for ${category} (${resources.length} items recovered). Retrying with maxCount=${GEMINI_RETRY_MAX_RESOURCES}`);
    const { resources: retryResources } = await _callGeminiAPIOnce(
      prompt, category, placeName, placeGeoContext, placeCoordinates, GEMINI_RETRY_MAX_RESOURCES
    );
    console.log(`🔄 [AIResourceGeneration] Retry result for ${category}: ${retryResources.length} items`);

    // Keep whichever attempt produced more. The retry asks for only
    // GEMINI_RETRY_MAX_RESOURCES, so returning it unconditionally could hand back
    // FEWER resources than the truncated first attempt already salvaged.
    return retryResources.length >= resources.length ? retryResources : resources;
  }

  return resources;
}

/**
 * Generates resources for a specific category
 * @param {string} userId - User ID
 * @param {string} tripId - Trip ID
 * @param {string} placeId - Place ID
 * @param {string} placeName - Place name
 * @param {string} placeGeoContext - Geographic context
 * @param {string} category - Resource category
 * @param {Object} tripContext - Trip context object
 * @param {Array} deletedUrls - Array of deleted URLs to exclude
 * @param {boolean} forceRefresh - Force refresh bypassing cache
 * @param {Object} placeCoordinates - Optional coordinates {lat, lng} for Maps Grounding
 * @returns {Promise<Array>} Generated resources
 */
async function generateResourcesForCategory(userId, tripId, placeId, placeName, placeGeoContext, category, tripContext, deletedUrls = [], forceRefresh = false, placeCoordinates = null) {
  try {
    // Check cache first (unless forceRefresh is true)
    const cacheKey = generateCacheKey(userId, tripId, placeId, category, tripContext);

    if (!forceRefresh) {
      const cached = await getCachedResources(cacheKey, category);

      if (cached) {
        console.log(`📋 [AIResourceGeneration] Using cached resources for ${category}:`, cached.resources);
        return cached.resources;
      }
    } else {
      console.log(`🔄 [AIResourceGeneration] Bypassing cache for ${category} (force refresh)`);
    }

    console.log(`🔄 [AIResourceGeneration] Generating fresh resources for ${category}`);

    // Get prompt configuration
    const promptConfig = await configManager.getCategoryConfig(category);
    if (!promptConfig) {
      console.warn(`⚠️ [AIResourceGeneration] No prompt configuration for category: ${category}`);
      return [];
    }

    // Build prompt with context, including deleted URLs
    const prompt = await configManager.buildPrompt(category, {
      placeName,
      placeGeoContext,  // Geographic context like "Tasmania, Australia" or "New York, USA"
      formattedDateRange: tripContext.formattedDateRange,
      duration: tripContext.duration,
      placeContext: tripContext.placeContext,
      deletedUrls: deletedUrls.length > 0 ? deletedUrls.join(', ') : null
    });

    // Parse AI response and clean up URLs (Gemini with Maps Grounding).
    // callGeminiAPI internally retries with a smaller count if the first response is truncated.
    const rawResources = await callGeminiAPI(prompt, category, placeName, placeGeoContext, placeCoordinates);
    
    // Clean and validate the parsed resources
    const cleanedResources = rawResources.map(resource => {
      let cleanedUrl = resource.url;
      let cleanedName = resource.name;

      // Clean name first - remove JSON fragments
      if (cleanedName) {
        // Remove JSON syntax from name (e.g., {"name": "Katz's Delicatessen", "url": ")
        if (cleanedName.includes('{"') || cleanedName.includes('"url"') || cleanedName.includes('"name"')) {
          // Try to extract just the name value
          const nameMatch = cleanedName.match(/(?:"name"\s*:\s*"([^"]+)"|^([^{":]+)(?=\s*[,{"]))/);
          if (nameMatch) {
            cleanedName = (nameMatch[1] || nameMatch[2]).trim();
          } else {
            // Fallback: remove all JSON syntax
            cleanedName = cleanedName.replace(/[{}"]/g, '').split(',')[0].split(':').pop().trim();
          }
        }

        // Remove any trailing commas or JSON artifacts
        cleanedName = cleanedName.replace(/[,;]+$/, '').trim();
      }

      // Fix malformed URLs (remove trailing brackets, fix markdown links)
      // Skip aggressive cleaning for properly formatted URLs (like our search URLs)
      if (cleanedUrl) {
        // Check if URL is already properly formatted (starts with http and has valid encoding)
        const isProperlyFormatted = cleanedUrl.startsWith('http://') || cleanedUrl.startsWith('https://');
        const encodedMatches = cleanedUrl.match(/[%][0-9A-Fa-f]{2}/g);
        const hasValidEncoding = !encodedMatches || encodedMatches.every(match => {
          // Check if encoded sequences are valid (like %20, %C3%A9, etc.)
          const code = parseInt(match.substring(1), 16);
          return code >= 0 && code <= 255;
        });
        
        if (!isProperlyFormatted || !hasValidEncoding) {
          // Only apply aggressive cleaning to malformed URLs
          // Remove trailing commas first (before other cleaning)
          cleanedUrl = cleanedUrl.replace(/[,;]+$/, '');

          // Remove trailing brackets
          cleanedUrl = cleanedUrl.replace(/\)+$/, '');

          // Fix URLs with JSON artifacts like %22%7D (match literal encoded strings, not individual chars)
          cleanedUrl = cleanedUrl.replace(/%22%7D/g, '').replace(/%7D/g, '');
          // Only remove specific problematic encoded sequences, not all % characters
          cleanedUrl = cleanedUrl.replace(/%22/g, '').replace(/%27/g, '').replace(/%7D/g, '').replace(/%7B/g, '');

          // Remove any remaining quote marks or braces
          cleanedUrl = cleanedUrl.replace(/['"]/g, '').replace(/[{}]/g, '');

          // Fix incomplete URLs that have JSON artifacts
          if (cleanedUrl.includes('\'"') || cleanedUrl.includes('\\\',"')) {
            cleanedUrl = cleanedUrl.split(/[',"]/)[0];
          }

          // Fix markdown-style links [text](url) -> extract URL
          const markdownMatch = cleanedUrl.match(/\[([^\]]+)\]\(([^)]+)\)/);
          if (markdownMatch) {
            cleanedName = markdownMatch[1];
            cleanedUrl = markdownMatch[2];
          }

          // Final cleanup: remove any trailing punctuation
          cleanedUrl = cleanedUrl.replace(/[,;.!?]+$/, '').trim();

          // Ensure URL has protocol
          if (cleanedUrl && !cleanedUrl.startsWith('http')) {
            cleanedUrl = 'https://' + cleanedUrl;
          }
        } else {
          // For properly formatted URLs, only do minimal cleanup
          cleanedUrl = cleanedUrl.trim();
        }
      }

      // Generate meaningful name if missing or generic
      if (!cleanedName || cleanedName.toLowerCase() === 'resource' || cleanedName.trim() === '') {
        if (cleanedUrl) {
          try {
            const urlObj = new URL(cleanedUrl);
            cleanedName = urlObj.hostname.replace('www.', '').split('.')[0];
            cleanedName = cleanedName.charAt(0).toUpperCase() + cleanedName.slice(1);
          } catch (e) {
            cleanedName = 'Resource';
          }
        } else {
          cleanedName = 'Resource';
        }
      }
      
      return {
        ...resource,
        name: cleanedName.trim(),
        url: cleanedUrl ? cleanedUrl.trim() : ''
      };
    }).filter(resource => resource.url && resource.name && resource.url.length > 0 && resource.name.length > 0);

    // Validate URLs (optional - skip if validation fails due to CORS)
    const validatedResources = [];
    const validationConfig = await configManager.getValidationConfig();
    
    if (validationConfig.validateUrls) {
      console.log(`🔍 [AIResourceGeneration] Validating ${cleanedResources.length} URLs for ${category}`);
      
      for (const resource of cleanedResources) {
        if (resource.url) {
          try {
            const isValid = await validateUrl(resource.url);
            if (isValid) {
              validatedResources.push({
                ...resource,
                id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
                createdAt: new Date().toISOString(),
                validated: true
              });
            } else {
              console.log(`⚠️ [AIResourceGeneration] Skipping invalid URL: ${resource.url}`);
            }
          } catch (error) {
            // If validation fails due to CORS, assume URL is valid and include it
            console.log(`⚠️ [AIResourceGeneration] URL validation failed for ${resource.url}, including anyway:`, error.message);
            validatedResources.push({
              ...resource,
              id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
              createdAt: new Date().toISOString(),
              validated: false // Mark as not validated due to CORS
            });
          }
        }
      }
    } else {
      // Skip validation, include all resources
      console.log(`⚠️ [AIResourceGeneration] URL validation disabled, including all ${cleanedResources.length} resources for ${category}`);
      for (const resource of cleanedResources) {
        if (resource.url) {
          validatedResources.push({
            ...resource,
            id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
            createdAt: new Date().toISOString(),
            validated: false // Mark as not validated
          });
        }
      }
    }

    // Filter out previously deleted resources
    const filteredResources = filterDeletedResources(userId, tripId, placeId, validatedResources);
    
    // Limit resources based on configuration
    const limitedResources = filteredResources.slice(0, promptConfig.maxResources);

    // Cache the results
    setCachedResources(cacheKey, limitedResources, category);

    console.log(`✅ [AIResourceGeneration] Generated ${limitedResources.length} resources for ${category}:`, limitedResources);
    return limitedResources;

  } catch (error) {
    console.error(`❌ [AIResourceGeneration] Error generating resources for ${category}:`, error);
    return [];
  }
}


/**
 * Clears the AI resource cache for a specific place or all places
 * @param {string} [userId] - User ID (optional, clears all if not provided)
 * @param {string} [tripId] - Trip ID (optional)
 * @param {string} [placeId] - Place ID (optional)
 */
export function clearAIResourceCache(userId, tripId, placeId) {
  // NOTE: this was dead code until 2026-08-18. `resourceCache` is a Map, but this
  // function used `Object.keys(resourceCache)` (always `[]` on a Map) and matched keys
  // built with `_` separators, while `generateCacheKey()` joins with `:`. Both faults
  // silently made it a no-op, so "Generate New AI Resources" never actually cleared the
  // cache it claims to. Left over from when the cache was a plain object.
  let clearedCount = 0;

  if (userId && tripId && placeId) {
    const prefix = `${userId}:${tripId}:${placeId}:`;
    for (const cacheKey of [...resourceCache.keys()]) {
      if (cacheKey.startsWith(prefix)) {
        resourceCache.delete(cacheKey);
        clearedCount++;
      }
    }
    console.log(`🗑️ [AIResourceGeneration] Cleared cache for place ${placeId}: ${clearedCount} entries`);
  } else {
    clearedCount = resourceCache.size;
    resourceCache.clear();
    console.log(`🗑️ [AIResourceGeneration] Cleared entire cache: ${clearedCount} entries`);
  }
}

/**
 * Main function to generate AI-powered resources for a place
 * @param {string} userId - **effectiveUserId**, not the raw auth uid. Both call sites in
 *   PlaceDetails.jsx pass `effectiveUserId` from `useTrip()`, so shared trips resolve to
 *   the owner's data correctly. The parameter name is legacy; do not "fix" it by passing
 *   `currentUser.uid` - that would break shared trips.
 * @param {string} tripId - Trip ID
 * @param {string} placeId - Place ID
 * @param {string} placeName - Place name
 * @param {string} placeGeoContext - Geographic context
 * @param {boolean} forceRefresh - Force refresh bypassing cache
 * @param {Object} placeCoordinates - Optional coordinates {lat, lng} for Maps Grounding
 * @returns {Promise<Object>} Generated resources organized by category
 */
export async function generateAIResources(userId, tripId, placeId, placeName, placeGeoContext = '', forceRefresh = false, placeCoordinates = null) {
  try {
    console.log(`🚀 [AIResourceGeneration] Starting AI resource generation for place: ${placeName} ${placeGeoContext ? `(${placeGeoContext})` : ''}`);
    if (forceRefresh) {
      console.log(`🔄 [AIResourceGeneration] Force refresh enabled - bypassing cache`);
    }

    // If coordinates weren't provided, try to fetch them from Firestore
    if (!placeCoordinates) {
      try {
        const placeRef = doc(db, 'users', userId, 'trips', tripId, 'places', placeId);
        const placeSnap = await getDoc(placeRef);
        if (placeSnap.exists()) {
          const placeData = placeSnap.data();
          if (placeData.latitude && placeData.longitude) {
            placeCoordinates = {
              lat: placeData.latitude,
              lng: placeData.longitude
            };
            console.log(`📍 [AIResourceGeneration] Fetched coordinates from Firestore: ${placeCoordinates.lat}, ${placeCoordinates.lng}`);
          } else {
            console.warn(`⚠️ [AIResourceGeneration] Place ${placeId} has no coordinates in Firestore`);
          }
        }
      } catch (coordError) {
        console.warn(`⚠️ [AIResourceGeneration] Could not fetch place coordinates:`, coordError);
      }
    }

    // Extract trip context
    const tripContext = await tripContextService.extractTripContext(userId, tripId, placeId);

    // Get deleted URLs for this place
    const deletedUrls = getDeletedResourceUrls(userId, tripId, placeId);
    console.log(`🚫 [AIResourceGeneration] Excluding ${deletedUrls.length} previously deleted URLs`);

    // Determine categories to generate
    const categories = await getCategoriesForContext(tripContext);

    // Ensure categories is an array
    if (!Array.isArray(categories)) {
      console.warn('⚠️ [AIResourceGeneration] getCategoriesForContext returned non-array:', categories);
      return { resources: {}, metadata: { error: 'Invalid categories returned' } };
    }

    console.log(`📋 [AIResourceGeneration] Generating resources for categories: ${categories.join(', ')}`);

    // Generate resources for each category
    const allResources = {};
    const generationPromises = categories.map(async (category) => {
      const resources = await generateResourcesForCategory(
        userId, tripId, placeId, placeName, placeGeoContext, category, tripContext, deletedUrls, forceRefresh, placeCoordinates
      );
      allResources[category] = resources || []; // Ensure it's always an array
    });

    await Promise.all(generationPromises);

    // Calculate totals
    const totalResources = Object.values(allResources).reduce(
      (total, resources) => total + (Array.isArray(resources) ? resources.length : 0), 0
    );

    const result = {
      resources: allResources,
      metadata: {
        placeId,
        placeName,
        tripContext,
        categories,
        totalResources,
        generatedAt: new Date().toISOString(),
        cacheHit: false // This would be calculated based on cache usage
      }
    };

    console.log(`✅ [AIResourceGeneration] Generated ${totalResources} total resources across ${categories.length} categories`);
    return result;

  } catch (error) {
    console.error('❌ [AIResourceGeneration] Error generating AI resources:', error);
    
    // Return empty result on error
    return {
      resources: {},
      metadata: {
        placeId,
        placeName,
        error: error.message,
        generatedAt: new Date().toISOString()
      }
    };
  }
}

/**
 * Gets cached resources for a place
 * @param {string} userId - User ID
 * @param {string} tripId - Trip ID
 * @param {string} placeId - Place ID
 * @returns {Object} Cached resources organized by category
 */
export function getCachedAIResources(userId, tripId, placeId) {
  const cachedResources = {};
  
  for (const [cacheKey, cached] of resourceCache.entries()) {
    if (cacheKey.startsWith(`${userId}:${tripId}:${placeId}:`)) {
      const category = cached.category;
      if (!cachedResources[category]) {
        cachedResources[category] = [];
      }
      cachedResources[category].push(...cached.resources);
    }
  }
  
  return cachedResources;
}

/**
 * Clears cache for a specific place or all places
 * @param {string} userId - User ID
 * @param {string} tripId - Trip ID
 * @param {string} placeId - Place ID (optional, clears all if not provided)
 */
export function clearResourceCache(userId, tripId, placeId = null) {
  let clearedCount = 0;
  
  for (const [cacheKey] of resourceCache.entries()) {
    const shouldClear = placeId ? 
      cacheKey.startsWith(`${userId}:${tripId}:${placeId}:`) :
      cacheKey.startsWith(`${userId}:${tripId}:`);
    
    if (shouldClear) {
      resourceCache.delete(cacheKey);
      clearedCount++;
    }
  }
  
  console.log(`🧹 [AIResourceGeneration] Cleared ${clearedCount} cache entries`);
}

/**
 * Gets cache statistics
 * @returns {Object} Cache statistics
 */
export function getCacheStats() {
  const stats = {
    totalEntries: resourceCache.size,
    categories: {},
    totalResources: 0
  };
  
  for (const [cacheKey, cached] of resourceCache.entries()) {
    const category = cached.category;
    if (!stats.categories[category]) {
      stats.categories[category] = { entries: 0, resources: 0 };
    }
    stats.categories[category].entries++;
    stats.categories[category].resources += cached.resources.length;
    stats.totalResources += cached.resources.length;
  }
  
  return stats;
}

// Export the service object
export const aiResourceGenerationService = {
  generateAIResources,
  getCachedAIResources,
  clearResourceCache,
  getCacheStats,
  validateUrl,
  parseAIResponse
};

export default aiResourceGenerationService;
